import {Component, forwardRef, ChangeDetectionStrategy, HostBinding, AfterViewInit, ElementRef, OnInit, signal, ChangeDetectorRef, input, model, output, viewChild} from '@angular/core'
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms'
import type { LucideIconData } from '@lucide/icons'
import { BaseIconComponent } from '../base-icon/base-icon.component'
import {parseToNumber} from '@/utils/number'

@Component({
	selector: 'base-input',
	templateUrl: './base-input.component.html',
	styleUrls: ['./base-input.component.css'],
	imports: [BaseIconComponent],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => BaseInputComponent),
			multi: true
		}
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseInputComponent implements ControlValueAccessor, AfterViewInit, OnInit {
	readonly type = input('text')
	readonly defaultValue = input('')
	readonly label = input('')
	readonly placeholder = input('')
	readonly id = input<string>()
	readonly iconRight = input<LucideIconData | null>(null)
	readonly iconLeft = input<LucideIconData | null>(null)
	readonly rightContentClickable = input(false)
	readonly leftContentClickable = input(false)
	readonly disabled = model(false)
	readonly required = input(false)
	readonly readonly = input(false)
	readonly autofocus = input(false)
	readonly autocomplete = input<AutoFill>('off')

	readonly changed = output<string | number>()
	readonly iconClicked = output<MouseEvent>()
	readonly focused = output<void>()
	readonly blured = output<void>()
	readonly onkeyup = output<Event>()

	readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl')

	// Bind generated id if none provided
	@HostBinding('attr.id')
	get inputId() {
		return this.id() || `base-input-${this._uid}`
	}
	private _uid = Math.random().toString(36).substring(2, 9)

	/**
   * Underlying value always kept as string for display.
   * For type="number", parsed value is emitted as number | null.
   */
	public value = ''
	public isFocused = signal(false)

	constructor(private cd: ChangeDetectorRef) {}

	ngOnInit(): void {
		// this.writeValue(this.defaultValue)
		const defaultValue = this.defaultValue()
		if (defaultValue) {
			this.value = defaultValue
		}
	}

	ngAfterViewInit(): void {
		const inputEl = this.inputEl()
		if (this.autofocus() && inputEl) {
			inputEl.nativeElement.focus()
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onChange: (value: string | number | null) => void = () => {}
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onTouched: () => void = () => {}

	writeValue(value: string | number): void {
		this.value = value !== null ? String(value) : ''
		this.onChange(this.value)
		this.changed.emit(this.value)
		this.cd.markForCheck()
	}

	registerOnChange(fn: (value: string | number | null) => void): void {
		this.onChange = fn
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled.set(isDisabled)
	}

	handleInput(event: Event): void {
		const input = event.target as HTMLInputElement
		const raw = input.value
		this.value = raw

		let parsed: string | number = raw
		if (this.type() === 'number') {
			const number = parseToNumber(raw, { integer: true })
			parsed = typeof number === 'number' ? number : ''
		}
		this.onChange(parsed)
		this.changed.emit(parsed)
	}

	handleBlur(state: boolean): void {
		this.onTouched()
		this.blured.emit()
		this.isFocused.set(state)
	}

	handleFocus(state: boolean): void {
		this.onTouched()
  	this.focused.emit()
		this.isFocused.set(state)
	}

	handleIconClick(event: MouseEvent): void {
		this.iconClicked.emit(event)
	}

	handleKeyUp(event: Event): void {
		this.onkeyup.emit(event)
	}
}
